import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { WorkTracker } from '@flyfreely-portal-ui/flyfreely';
import {
    CellStyleModule,
    ClientSideRowModelModule,
    ColDef,
    ColumnApiModule,
    CustomFilterModule,
    DragStoppedEvent,
    EventApiModule,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ModuleRegistry,
    RowClassRules,
    RowSelectionModule,
    RowSelectionOptions,
    RowStyleModule,
    SelectionChangedEvent,
    SelectionColumnDef,
    SortChangedEvent,
    SortDirection,
    TextFilterModule,
    themeQuartz
} from 'ag-grid-community';
import { Angulartics2 } from 'angulartics2';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ColumnTemplateDirective } from '../column-template.directive';
import {
    compareRenderedData,
    initColumn,
    normaliseDateSearchValues,
    normaliseTableSearchValues,
    resolveValue,
    setupSearch
} from '../helpers';
import {
    AvailableColumn,
    BulkTableAction,
    ColumnSelection,
    ColumnSortPreferences,
    isTableColumnGroup,
    TableColumn,
    TableConfig,
    TableSearchValues
} from '../interfaces';
import { RowActionsDirective } from '../row-actions.directive';
import {
    ColumnPickerHeader,
    ColumnPickerHost
} from './column-picker-header.component';
import { LinkCellRenderer } from './link-cell-renderer.component';
import {
    RowActionsCellRenderer,
    RowActionsHost
} from './row-actions-cell-renderer.component';
import {
    FloatingFilterHost,
    TableFloatingFilter
} from './table-floating-filter.component';
import { TableHeaderComponent } from './table-header.component';
import { TemplateCellRenderer } from './template-cell-renderer.component';

// AG Grid 33+ is modular: register only what this table uses. Column moving,
// resizing and the header sort UI are part of the core and need no module.
ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    RowSelectionModule,
    ColumnApiModule,
    EventApiModule,
    RowStyleModule,
    CellStyleModule,
    CustomFilterModule,
    TextFilterModule
]);

// static-table's rows: 28px with zebra striping, 50px in comfortable mode;
// its heading row is 30px
const ROW_HEIGHT_COMPACT = 28;
const ROW_HEIGHT_COMFORTABLE = 50;
const HEADER_HEIGHT = 32;
// static-table's search row: 30px controls with 6px above and below
const FLOATING_FILTERS_HEIGHT = 42;
const MIN_LIMIT = 5;
const SEARCH_DEBOUNCE = 700;
const RESIZE_DEBOUNCE = 350;

// The existing bootstrap table look (libs/ui/src/lib/styles/_custom-table.scss
// %table-cells and the bootstrap variables): tundora text, 14px, semi-bold
// headings, #ddd borders, $gray-lightest zebra stripes, no hover highlight
const TEXT_COLOR = '#4a4a4a';
const BORDER_COLOR = '#ddd';
const ZEBRA_COLOR = '#f5f5f5';

/** The static-table scanability modes: zebra striping or plain rows */
export type ScanType = 'alternate' | 'comfortable';

function hasSearchValue(value: unknown): boolean {
    return (
        value != null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );
}

const MULTI_ROW_SELECTION: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: true,
    headerCheckbox: true,
    enableClickSelection: false
};

/**
 * A row pre-rendered for display: `data` holds the original row, `rowId` a
 * stable identity for the grid, and every other key the formatted string for
 * that column. Mirrors static-table's approach so plain cells are property
 * reads, not per-change-detection formatter calls.
 */
export interface RenderedRow<R = unknown> {
    data: R;
    rowId: string;
    /** The formatted cell text by column key; the grid reads it by `field` */
    [key: string]: unknown;
}

/**
 * The Data Table is the AG Grid Community based successor to `static-table`.
 * It accepts the same column and table configuration interfaces and the same
 * `columnTemplate` / `rowActions` content templates, so an existing
 * `<static-table>` migrates by swapping the tag.
 *
 * Columns can be reordered by dragging; the order is reported through
 * `columnOrderChanged` for the consumer to persist. In viewport mode the page
 * size is derived from the available height (`pageSizeChanged`) so the table
 * exactly fills its container.
 *
 * Like static-table it has two modes. With `tableConfig.serverPagination` or
 * `totalItems` set, paging, sorting and filtering are the consumer's job and
 * the grid only holds the current page. Otherwise the table filters, sorts
 * and pages the full `tableData` itself.
 *
 * The grid's header, floating filter and action cell components receive this
 * instance as the grid `context` (`[context]="this"` in the template) and keep
 * it as their `host`: the column picker calls `toggle` and `isColumnChecked`,
 * the floating filter binds a control from `filterForm`, the actions cell
 * reads `tableConfig.actions`. The `*Host` interfaces declared beside those
 * components list what they use, which is why members with no references in
 * this file are still public.
 */
@Component({
    selector: 'data-table',
    templateUrl: './data-table.component.html',
    styleUrl: './data-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTable<R = any>
    implements
        OnInit,
        OnChanges,
        AfterContentInit,
        AfterViewInit,
        OnDestroy,
        ColumnPickerHost,
        FloatingFilterHost,
        RowActionsHost
{
    /** Columns, or named groups of columns toggled together in the picker */
    @Input() availableColumns: AvailableColumn[];
    @Input() tableData: R[];
    @Input() tableConfig: TableConfig;
    @Input() columnSorting?: ColumnSortPreferences;
    @Input() tableSearch?: TableSearchValues;
    @Input() selectedColumns?: string[];
    @Input() columnOrder?: string[];
    @Input() templateActionsWidth = 0;
    @Input() hideActionColumn = false;
    @Input() centeredHeadings = false;
    @Input() centeredColumns = false;
    @Input() borderless = false;
    @Input() underlinedRows = false;
    /**
     * 'SELECTABLE' shows the two footer buttons that switch between zebra
     * striping ('alternate') and plain rows ('comfortable'). Unlike
     * static-table the row height never changes: the viewport-driven page
     * size depends on it being constant.
     */
    @Input() scanability: 'SELECTABLE' | ScanType = 'SELECTABLE';

    @Output() pageChanged = new EventEmitter<number>();
    @Output() sortOrderChanged = new EventEmitter<ColumnSortPreferences>();
    @Output() search = new EventEmitter<TableSearchValues>();
    @Output() selectedColumnsChanged = new EventEmitter<ColumnSelection>();
    @Output() columnOrderChanged = new EventEmitter<string[]>();
    @Output() pageSizeChanged = new EventEmitter<number>();
    /** Emitted only in fixed page size mode, when the user picks a page size */
    @Output() itemLimitUpdated = new EventEmitter<number>();

    @ContentChildren(ColumnTemplateDirective)
    templates: QueryList<ColumnTemplateDirective>;
    @ContentChild(RowActionsDirective) rowActionsTemplate: RowActionsDirective;

    @ViewChild('scrollRegion', { static: true })
    scrollRegion: ElementRef<HTMLDivElement>;

    activeScan: ScanType = 'alternate';
    theme = this.buildTheme();
    readonly headerHeight = HEADER_HEIGHT;
    readonly floatingFiltersHeight = FLOATING_FILTERS_HEIGHT;
    /** The scan mode decides the row height, as in static-table */
    get rowHeight(): number {
        return this.activeScan === 'comfortable'
            ? ROW_HEIGHT_COMFORTABLE
            : ROW_HEIGHT_COMPACT;
    }
    getRowHeight = () => this.rowHeight;
    readonly sortingOrder: SortDirection[] = ['asc', 'desc'];
    readonly defaultColDef: ColDef = {
        flex: 1,
        minWidth: 110,
        resizable: true,
        // Dragging a column to an edge must reorder it, not pin it beside
        // the selection and action columns
        lockPinned: true,
        sortable: false,
        suppressHeaderMenuButton: true
    };
    /** Row selection exists only to drive bulk actions, as in static-table */
    rowSelection?: RowSelectionOptions;
    readonly selectionColumnDef: SelectionColumnDef = {
        width: 40,
        maxWidth: 40,
        flex: 0,
        pinned: 'left',
        lockPosition: 'left',
        suppressMovable: true,
        resizable: false
    };
    readonly rowClassRules: RowClassRules<RenderedRow<R>> = {
        'row-warning': p =>
            this.tableConfig?.isWarningFunction?.(p.data.data) ?? false,
        unseen: p =>
            this.tableConfig?.isSeenFunction != null &&
            !this.tableConfig.isSeenFunction(p.data.data)
    };
    getRowId = (p: GetRowIdParams<RenderedRow<R>>) => p.data.rowId;

    // Selection filter values can be objects (e.g. collectedBy's {id, type})
    // whose references change whenever searchOptions are rebuilt
    compareSearchOption = (a: unknown, b: unknown) =>
        JSON.stringify(a) === JSON.stringify(b);

    /** The rows the grid shows: the whole page in server mode, else a slice */
    rows: RenderedRow<R>[] = [];
    clientTotalItems = 0;
    /** The user's page size in fixed page size mode (tableConfig.limitSelection) */
    itemLimit?: number;
    columnDefs: ColDef[] = [];
    displayColumns: TableColumn[] = [];
    /** Picker entries: selectable standalone columns and selectable groups */
    selectableColumns: AvailableColumn[] = [];
    selected: RenderedRow<R>[] = [];
    /**
     * One control per searchable display column, named by column key. Keys
     * may contain dots (a dot-notation `value` with no explicit `key`), so
     * controls are always read as `controls[key]`: `get(key)` would walk the
     * dots as a nested path and find nothing.
     */
    filterForm: FormGroup = new FormGroup({});

    working = false;

    private api?: GridApi;
    private workTracker = new WorkTracker();
    private allColumns: TableColumn[] = [];
    private columnDefinitions: AvailableColumn[] = [];
    /** Child column key -> owning group key, for visibility and ordering */
    private columnToGroupKey = new Map<string, string>();
    /** Every rendered row; the client-side pipeline derives `rows` from it */
    private allRows: RenderedRow<R>[] = [];
    private clientSearch: TableSearchValues = {};
    private clientSorting?: ColumnSortPreferences;
    private clientPage = 0;
    private clientLimit?: number;
    private visibleKeys: string[] = [];
    private templateLookup: { [key: string]: TemplateRef<unknown> } = {};
    private regionHeight$ = new Subject<number>();
    private resizeObserver?: ResizeObserver;
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private ngZone: NgZone,
        private angulartics: Angulartics2,
        private changeDetector: ChangeDetectorRef
    ) {}

    get selectedRows(): R[] {
        return this.selected.map(r => r.data);
    }

    /** static-table semantics: any server hint switches paging to the consumer */
    get clientMode(): boolean {
        return !(
            this.tableConfig?.serverPagination ||
            this.tableConfig?.totalItems != null
        );
    }

    get showPagination(): boolean {
        return this.tableConfig?.limit != null;
    }

    /**
     * static-table's page size picker. With `limitSelection` the page size is
     * the user's choice instead of being derived from the viewport height, and
     * rows that do not fit scroll inside the grid.
     */
    get fixedPageSize(): boolean {
        return this.showPagination && this.tableConfig?.limitSelection != null;
    }

    /**
     * Without a viewport-driven page size the table grows with its rows and
     * the page scrolls, as static-table does. That is what lets it sit in a
     * panel or page section that has no fixed height.
     */
    @HostBinding('class.auto-height')
    get autoHeight(): boolean {
        return !this.showPagination || this.fixedPageSize;
    }

    get pageLimit(): number | undefined {
        return this.clientMode
            ? this.clientLimit ?? this.tableConfig?.limit
            : this.tableConfig?.limit;
    }

    get footerTotalItems(): number {
        return this.clientMode
            ? this.clientTotalItems
            : this.tableConfig?.totalItems ?? 0;
    }

    get footerCurrentPage(): number {
        return this.clientMode
            ? this.clientPage
            : this.tableConfig?.currentPage ?? 0;
    }

    get actionsWidth(): number {
        // 36px per icon button plus the reduced cell padding (see .actions-cell).
        // Config actions and template buttons sit side by side, so size for
        // both; static-table's "actions-wide" cell covered that case.
        const buttons = Math.max(
            1,
            (this.tableConfig?.actions?.length ?? 0) + this.templateActionsWidth
        );
        return buttons * 36 + 16;
    }

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => {
                this.working = working;
                this.changeDetector.markForCheck();
            });
        // Client mode filters as the user types; the debounced emit below
        // is what consumers persist as preferences in either mode
        this.filterForm.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(values => {
                if (!this.clientMode) {
                    return;
                }
                this.clientSearch = normaliseTableSearchValues(
                    values,
                    this.tableConfig?.timeZone
                );
                this.clientPage = 0;
                this.applyClientView();
            });
        this.filterForm.valueChanges
            .pipe(
                debounceTime(SEARCH_DEBOUNCE),
                distinctUntilChanged(
                    (a, b) => JSON.stringify(a) === JSON.stringify(b)
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(values => {
                this.search.emit(
                    normaliseTableSearchValues(
                        values,
                        this.tableConfig?.timeZone
                    )
                );
                // Consumers reset their page in place on a search; show that
                // in the footer now rather than when the response arrives
                this.changeDetector.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        const columnsChanged =
            changes.availableColumns ||
            changes.selectedColumns ||
            changes.columnOrder;
        if (columnsChanged) {
            this.rebuildColumns();
        } else if (
            changes.columnSorting ||
            changes.templateActionsWidth ||
            changes.hideActionColumn ||
            changes.centeredHeadings ||
            changes.centeredColumns
        ) {
            this.buildColumnDefs();
        }
        if (changes.scanability && this.scanability !== 'SELECTABLE') {
            this.activeScan = this.scanability;
        }
        if (
            changes.borderless ||
            changes.underlinedRows ||
            changes.scanability
        ) {
            this.theme = this.buildTheme();
        }
        if (changes.tableData || columnsChanged) {
            this.renderRows();
        }
        if (changes.tableData) {
            // New results mean the page, filters, or tab changed, so any
            // previous selection may reference rows that are gone.
            this.selected = [];
            this.api?.deselectAll();
        }
        if (changes.columnSorting) {
            this.clientSorting = this.columnSorting;
            if (this.clientMode) {
                this.applyClientView();
            }
        }
        if (changes.tableConfig) {
            this.rowSelection =
                this.tableConfig?.bulkActions?.length > 0
                    ? MULTI_ROW_SELECTION
                    : undefined;
            if (this.fixedPageSize) {
                // The consumer owns the limit (it persists itemLimitUpdated)
                this.itemLimit = this.tableConfig.limit;
                this.clientLimit = this.tableConfig.limit;
            }
            if (!changes.tableData && !columnsChanged) {
                this.updateRows();
            }
        }
        if (changes.tableSearch && this.tableSearch != null) {
            // Date pickers need Date objects, not the ISO strings we emit
            this.filterForm.patchValue(
                normaliseDateSearchValues(this.tableSearch, this.allColumns),
                { emitEvent: false }
            );
            if (this.clientMode) {
                // Nothing emitted, so apply the restored values to the rows
                // here. A consumer feeding back the search we emitted is a
                // no-op, which keeps the user's page.
                const search = normaliseTableSearchValues(
                    this.filterForm.value,
                    this.tableConfig?.timeZone
                );
                if (
                    JSON.stringify(search) !== JSON.stringify(this.clientSearch)
                ) {
                    this.clientSearch = search;
                    this.clientPage = 0;
                    this.applyClientView();
                }
            }
        }
    }

    ngAfterContentInit() {
        const buildLookup = () => {
            this.templateLookup = this.templates.reduce(
                (acc, t) => ({ ...acc, [t.column]: t.tpl }),
                {}
            );
            // Column defs carry the template refs, so rebuild once known
            this.buildColumnDefs();
            this.changeDetector.markForCheck();
        };
        buildLookup();
        this.templates.changes
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(buildLookup);
    }

    ngAfterViewInit() {
        this.regionHeight$
            .pipe(debounceTime(RESIZE_DEBOUNCE), takeUntil(this.ngUnsubscribe$))
            .subscribe(height => this.applyRegionHeight(height));
        this.resizeObserver = new ResizeObserver(([entry]) =>
            this.regionHeight$.next(entry.contentRect.height)
        );
        this.resizeObserver.observe(this.scrollRegion.nativeElement);
        // Report the page size straight away so a consumer can make its first
        // request with it, instead of fetching a default page and refetching
        // once the debounced observer fires. Deferred a microtask so the
        // consumer's bindings are not changed while they are being checked.
        const initialHeight = this.scrollRegion.nativeElement.clientHeight;
        queueMicrotask(() => this.applyRegionHeight(initialHeight, true));
    }

    /**
     * Turn the available height into a page size: in client mode re-page the
     * rows, in server mode tell the consumer (viewport mode only).
     *
     * The initial measurement is always applied, even when it matches the
     * current limit: a consumer may be waiting for it before its first fetch.
     * Later resizes only apply when the page size actually changes.
     */
    private applyRegionHeight(height: number, initial = false) {
        if (!this.showPagination || this.fixedPageSize) {
            return;
        }
        const limit = Math.max(
            MIN_LIMIT,
            Math.floor(
                (height - HEADER_HEIGHT - FLOATING_FILTERS_HEIGHT) /
                    this.rowHeight
            )
        );
        // ResizeObserver callbacks run outside the Angular zone
        this.ngZone.run(() => {
            if (this.clientMode) {
                if (initial || limit !== this.clientLimit) {
                    this.clientLimit = limit;
                    this.applyClientView();
                }
            } else if (initial || limit !== this.tableConfig.limit) {
                this.pageSizeChanged.emit(limit);
            }
        });
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    columnId(definition: AvailableColumn): string {
        return this.definitionKey(definition);
    }

    isColumnChecked(definition: AvailableColumn): boolean {
        return this.visibleKeys.includes(this.definitionKey(definition));
    }

    /**
     * Show or hide a picker entry. As in static-table the table applies the
     * change itself and emits it for persisting: a consumer that feeds the
     * selection back causes a no-op rebuild, and one that does not (or binds
     * this table to another list, as the personnel invites table does) still
     * sees the column appear.
     */
    toggle(definition: AvailableColumn) {
        const key = this.definitionKey(definition);
        const selectedColumns = this.visibleKeys.includes(key)
            ? this.visibleKeys.filter(k => k !== key)
            : [...this.visibleKeys, key];
        this.selectedColumns = selectedColumns;
        this.rebuildColumns();
        this.changeDetector.markForCheck();
        this.selectedColumnsChanged.emit({ selectedColumns });
        this.angulartics.eventTrack.next({ action: 'table-columns-change' });
    }

    setScanType(type: ScanType) {
        this.activeScan = type;
        this.theme = this.buildTheme();
        this.api?.resetRowHeights();
        // A different row height means a different number of rows fit
        this.applyRegionHeight(this.scrollRegion.nativeElement.clientHeight);
    }

    setItemLimit(limit: number) {
        this.itemLimit = limit;
        if (this.clientMode) {
            this.clientLimit = limit;
            this.clientPage = 0;
            this.applyClientView();
        }
        this.itemLimitUpdated.emit(limit);
    }

    onFooterPageChanged(page: number) {
        // ngx-bootstrap's pagination is 1-based and re-emits on model updates
        if (page - 1 === this.footerCurrentPage) {
            return;
        }
        if (this.clientMode) {
            this.clientPage = page - 1;
            this.applyClientView();
        } else {
            this.pageChanged.emit(page - 1);
        }
    }

    onGridReady(event: GridReadyEvent) {
        this.api = event.api;
    }

    onSortChanged(event: SortChangedEvent) {
        const sorted = event.api.getColumnState().find(s => s.sort != null);
        if (sorted == null) {
            return;
        }
        const sorting = {
            column: sorted.colId,
            ascending: sorted.sort === 'asc'
        };
        const current = this.clientMode
            ? this.clientSorting
            : this.columnSorting;
        if (
            sorting.column === current?.column &&
            sorting.ascending === current?.ascending
        ) {
            return;
        }
        if (this.clientMode) {
            this.clientSorting = sorting;
            this.clientPage = 0;
            this.applyClientView();
        }
        this.sortOrderChanged.emit(sorting);
        this.changeDetector.markForCheck();
    }

    /**
     * Column order is kept as top-level keys: a dragged group member moves
     * its whole group, which snaps back together when the consumer feeds
     * the new order in.
     */
    onDragStopped(event: DragStoppedEvent) {
        const dataKeys = new Set(this.displayColumns.map(c => c.key));
        const order = event.api
            .getColumnState()
            .map(s => s.colId)
            .filter(id => dataKeys.has(id))
            .map(id => this.topLevelKey(id))
            .filter((key, i, all) => all.indexOf(key) === i);
        const current = this.displayColumns
            .map(c => this.topLevelKey(c.key))
            .filter((key, i, all) => all.indexOf(key) === i);
        if (order.join() === current.join()) {
            return;
        }
        this.columnOrderChanged.emit(order);
    }

    onSelectionChanged(event: SelectionChangedEvent) {
        this.selected = event.api.getSelectedRows();
    }

    /**
     * Execute the bulk table action against the selected rows, tracking
     * promise-returning actions as work in progress.
     */
    doBulkAction(action: BulkTableAction<R>) {
        const response = action.action(this.selectedRows);
        if (response instanceof Promise) {
            const doneWorking = this.workTracker.createTracker();
            response.finally(doneWorking);
        }
    }

    private rebuildColumns() {
        this.columnDefinitions = this.availableColumns ?? [];
        this.allColumns = this.columnDefinitions
            .reduce<TableColumn[]>(
                (acc, definition) =>
                    isTableColumnGroup(definition)
                        ? [...acc, ...definition.columns]
                        : [...acc, definition],
                []
            )
            .map(column => initColumn(column));
        this.columnToGroupKey = new Map();
        this.columnDefinitions
            .filter(isTableColumnGroup)
            .forEach(group =>
                group.columns.forEach(column =>
                    this.columnToGroupKey.set(initColumn(column).key, group.key)
                )
            );
        this.selectableColumns = this.columnDefinitions.filter(
            definition => definition.selectable
        );
        this.visibleKeys = this.selectedColumns ?? this.defaultSelectedKeys();

        const byKey = new Map(this.allColumns.map(c => [c.key, c]));
        const orderIx = (key: string) => {
            const ix = this.columnOrder?.indexOf(key) ?? -1;
            return ix === -1 ? Number.MAX_SAFE_INTEGER : ix;
        };
        // Stable sort of the top-level definitions: ordered keys first, the
        // rest keep definition order; group members stay together
        const orderedDefinitions = this.columnDefinitions
            .map((definition, i) => ({
                definition,
                i,
                key: this.definitionKey(definition)
            }))
            .sort((a, b) => orderIx(a.key) - orderIx(b.key) || a.i - b.i)
            .map(x => x.definition);

        this.displayColumns = orderedDefinitions.reduce<TableColumn[]>(
            (acc, definition) => {
                if (isTableColumnGroup(definition)) {
                    return this.visibleKeys.includes(definition.key)
                        ? [
                              ...acc,
                              ...definition.columns.map(c =>
                                  byKey.get(initColumn(c).key)
                              )
                          ]
                        : acc;
                }
                const column = byKey.get(initColumn(definition).key);
                // As in static-table, non-selectable standalone columns are
                // always shown; selectable ones only when selected
                return !column.selectable ||
                    this.visibleKeys.includes(column.key)
                    ? [...acc, column]
                    : acc;
            },
            []
        );
        this.syncFilterControls();
        this.buildColumnDefs();
    }

    /** static-table's default: selectable entries flagged defaultSelection */
    private defaultSelectedKeys(): string[] {
        return this.columnDefinitions
            .filter(
                definition =>
                    definition.selectable && definition.defaultSelection
            )
            .map(definition => this.definitionKey(definition));
    }

    private definitionKey(definition: AvailableColumn): string {
        return isTableColumnGroup(definition)
            ? definition.key
            : initColumn(definition).key;
    }

    private topLevelKey(columnKey: string): string {
        return this.columnToGroupKey.get(columnKey) ?? columnKey;
    }

    /**
     * Column defs follow displayColumns order, so re-emitting them after a
     * reorder or visibility change is the whole synchronisation.
     *
     * The action column's existence and width are structural and fixed here;
     * its buttons are read live by RowActionsCellRenderer from the grid
     * context, so changing `tableConfig.actions` needs no rebuild.
     */
    private buildColumnDefs() {
        const dataColumns: ColDef[] = this.displayColumns.map(col => {
            const template = this.templateLookup[col.key];
            const searchable = col.searchable !== false;
            return {
                colId: col.key,
                field: col.key,
                headerName: col.name,
                headerComponent: TableHeaderComponent,
                headerComponentParams: {
                    tableColumn: col,
                    centered: this.centeredHeadings
                },
                sortable: col.sortable !== false,
                // Sorting is external: never let the grid re-sort the page
                comparator: () => 0,
                sort:
                    this.columnSorting?.column === col.key
                        ? this.columnSorting.ascending
                            ? 'asc'
                            : 'desc'
                        : null,
                ...this.cellRendererFor(col, template),
                cellClass: [
                    ...(template != null ? ['template-cell'] : []),
                    ...(this.centeredColumns ? ['text-centered'] : [])
                ],
                floatingFilter: searchable,
                // A column filter must exist for the floating filter row to
                // render; its model is never used
                filter: searchable ? 'agTextColumnFilter' : false,
                suppressFloatingFilterButton: true,
                floatingFilterComponent: TableFloatingFilter,
                floatingFilterComponentParams: { tableColumn: col }
            };
        });
        const actions = this.tableConfig?.actions ?? [];
        // As in static-table, the column also exists to host the column
        // picker in its heading
        const showActionColumn =
            !this.hideActionColumn &&
            (actions.length > 0 ||
                this.rowActionsTemplate != null ||
                this.selectableColumns.length > 0);
        const actionsColumn: ColDef[] = showActionColumn
            ? [
                  {
                      colId: '__actions',
                      headerName: '',
                      headerComponent: ColumnPickerHeader,
                      width: this.actionsWidth,
                      flex: 0,
                      sortable: false,
                      resizable: false,
                      suppressMovable: true,
                      pinned: 'right',
                      lockPosition: 'right',
                      cellClass: this.centeredColumns
                          ? ['actions-cell', 'text-centered']
                          : 'actions-cell',
                      cellRenderer: RowActionsCellRenderer,
                      cellRendererParams: {
                          template: this.rowActionsTemplate?.tpl
                      }
                  }
              ]
            : [];
        this.columnDefs = [...dataColumns, ...actionsColumn];
    }

    /**
     * How a plain data cell renders, in static-table's order of precedence:
     * a consumer template wins, then a link column, otherwise the grid's own
     * text rendering (no component, which is also the fastest path).
     */
    private cellRendererFor(
        col: TableColumn,
        template?: TemplateRef<unknown>
    ): Pick<ColDef, 'cellRenderer' | 'cellRendererParams'> {
        if (template != null) {
            return {
                cellRenderer: TemplateCellRenderer,
                cellRendererParams: { template, tableColumn: col }
            };
        }
        if (col.linkFunction != null) {
            return {
                cellRenderer: LinkCellRenderer,
                cellRendererParams: { linkFunction: col.linkFunction }
            };
        }
        return {};
    }

    /**
     * The grid theme carries the static-table layout flags: borderless,
     * underlined rows and the zebra striping of the 'alternate' scan mode.
     */
    private buildTheme() {
        return themeQuartz.withParams({
            headerHeight: HEADER_HEIGHT,
            rowHeight: this.rowHeight,
            fontFamily: 'inherit',
            fontSize: 14,
            headerFontSize: 14,
            headerFontWeight: 600,
            foregroundColor: TEXT_COLOR,
            headerTextColor: TEXT_COLOR,
            borderColor: BORDER_COLOR,
            cellHorizontalPadding: 10,
            rowHoverColor: 'transparent',
            spacing: 6,
            borderRadius: 2,
            oddRowBackgroundColor:
                this.activeScan === 'alternate' ? ZEBRA_COLOR : 'transparent',
            // The bootstrap table draws no outer, row or column borders;
            // underlined rows are opt-in and the 2px rule under the headings
            // is drawn in the stylesheet (the grid would also underline the
            // filter row)
            rowBorder:
                this.underlinedRows && !this.borderless
                    ? { color: '#ced4da' }
                    : false,
            headerRowBorder: false,
            wrapperBorder: false,
            columnBorder: false,
            pinnedColumnBorder: false
        });
    }

    private renderRows() {
        const defaultFormatter = (val: unknown) =>
            val != null ? String(val) : '';
        this.allRows = (this.tableData ?? []).map((row, index) =>
            this.allColumns.reduce(
                (acc, col) => ({
                    ...acc,
                    [col.key]:
                        (col.formatterFunction ?? defaultFormatter)(
                            resolveValue(row, col.value),
                            row
                        ) ?? ''
                }),
                // Rows without an id fall back to their position in the page
                {
                    data: row,
                    rowId: String((row as { id?: unknown })?.id ?? index)
                } as RenderedRow<R>
            )
        );
        this.clientPage = 0;
        this.updateRows();
    }

    private updateRows() {
        if (this.clientMode) {
            this.applyClientView();
        } else {
            this.rows = this.allRows;
            this.changeDetector.markForCheck();
        }
    }

    /**
     * Client mode: filter, sort and page the rendered rows the way
     * static-table does (same search helpers, same compare semantics), then
     * hand the current page to the grid.
     */
    private applyClientView() {
        const matches = setupSearch(this.allColumns, this.clientSearch);
        const filtered = this.allRows.filter(row => matches(row.data));

        const sorting = this.clientSorting;
        if (sorting != null) {
            const column = this.allColumns.find(c => c.key === sorting.column);
            const compare = column?.compareFunction ?? compareRenderedData;
            const order = sorting.ascending ? 1 : -1;
            filtered.sort(
                (a, b) =>
                    compare(
                        {
                            rawData: (a.data as Record<string, unknown>)[
                                sorting.column
                            ],
                            renderedData: a[sorting.column] as string
                        },
                        {
                            rawData: (b.data as Record<string, unknown>)[
                                sorting.column
                            ],
                            renderedData: b[sorting.column] as string
                        }
                    ) * order
            );
        }

        this.clientTotalItems = filtered.length;
        const limit = this.pageLimit;
        if (limit == null) {
            this.clientPage = 0;
            this.rows = filtered;
        } else {
            const pageCount = Math.max(1, Math.ceil(filtered.length / limit));
            this.clientPage = Math.min(this.clientPage, pageCount - 1);
            this.rows = filtered.slice(
                this.clientPage * limit,
                (this.clientPage + 1) * limit
            );
        }
        this.selected = [];
        this.api?.deselectAll();
        this.changeDetector.markForCheck();
    }

    private syncFilterControls() {
        const wanted = this.displayColumns
            .filter(c => c.searchable !== false)
            .map(c => c.key);
        for (const key of Object.keys(this.filterForm.controls)) {
            if (!wanted.includes(key)) {
                const control = this.filterForm.controls[key];
                // Hiding a column withdraws its filter. Clear it first so the
                // emitted search carries an explicit null for the key: a
                // consumer that merges criteria (the mission list) needs that
                // to drop the filter, a missing key leaves it applied.
                // static-table does the same.
                if (hasSearchValue(control.value)) {
                    control.setValue(null);
                }
                this.filterForm.removeControl(key, { emitEvent: false });
            }
        }
        for (const key of wanted) {
            if (this.filterForm.controls[key] == null) {
                this.filterForm.addControl(key, new FormControl(null), {
                    emitEvent: false
                });
            }
        }
        // As in static-table, a selected option that no longer exists in the
        // column's searchOptions is cleared. This emits so the stale filter is
        // also dropped from the consumer's search.
        for (const col of this.displayColumns) {
            if (col.searchable !== 'selection') {
                continue;
            }
            const control = this.filterForm.controls[col.key];
            const value = control?.value;
            if (value == null) {
                continue;
            }
            const stillAvailable = (col.searchOptions ?? []).some(option =>
                this.compareSearchOption(option.value, value)
            );
            if (!stillAvailable) {
                control.setValue(null);
            }
        }
    }
}
